//@ts-check
/* eslint-disable jsx-a11y/media-has-caption */
import type { ReactNode } from 'react';
import React, { lazy, useEffect, useState, Fragment, useRef, useLayoutEffect, Suspense } from 'react';
import { MOBILE_BREAKPOINT } from '../../../../utils/constants';
import { useWindowSize, useSearchParamHelpers } from '../../../_shared/react-hooks';
import { IPdpData, TUserSelections } from '../../types/PDP';
import { AlmostGoneImageMessage } from '../AlmostGone';
import CustomScrollBar from '../CustomScrollBar';
import { UseComputePreviewImagesType, getVideosByColor } from './logic';

const ModelChangerUI = lazy(
  () =>
    import(
      /* webpackChunkName: "ModelChangerUI" */
      '../ModelChanger/ModelChangerUI'
    )
);

const FullScreenPhotoSwipe = lazy(
  () =>
    import(
      /* webpackChunkName: "PdpFullScreenPhotoSwipe" */
      './FullScreenPhotoSwipe'
    )
);

type ProductImagesTypes = {
  pdpData: IPdpData;
  userSelections: TUserSelections;
  children: ReactNode;
  styleId: string;
  imagesData: UseComputePreviewImagesType;
  forceSuppressModelChanger: boolean;
};

const ProductImages = ({
  pdpData,
  userSelections,
  children,
  styleId,
  imagesData,
  forceSuppressModelChanger,
}: ProductImagesTypes) => {
  const slider: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  const viewPortWidth: number = useWindowSize().width;
  const isMobile = viewPortWidth < MOBILE_BREAKPOINT;

  const playVideo = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;
    const video = target.parentElement?.querySelector('video');
    if (video) {
      video.play();
      target.style.display = 'none';
      video.setAttribute('controls', 'true');
    }
  };

  // ? [REVISIT] video sizing adjustment; ideas: fixed aspect ratio, pure css
  useEffect(() => {
    adjustSlideshowHeight();
    // ? [REVISIT]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPortWidth]);

  useLayoutEffect(() => {
    adjustVideoHeight();
  }, [viewPortWidth]);

  const adjustSlideshowHeight = () => {
    if (slider.current && isMobile) {
      const height = (slider.current.firstChild as HTMLElement)?.getBoundingClientRect().height;
      slider.current.style.minHeight = `${height}px`;
    }
  };

  const adjustVideoHeight = () => {
    const videos: HTMLVideoElement[] = Array.from(document.querySelectorAll('.featured__video')); // We're really working with DOM and styles here.
    if (videos.length > 0) {
      videos.forEach((video) => {
        const wrapperHeight =
          slider.current && slider.current.firstElementChild
            ? slider.current.firstElementChild.getBoundingClientRect().height
            : 0;
        video.style.height = wrapperHeight !== 0 ? `${wrapperHeight}px` : 'initial';
      });
    }
  };

  const adjustHeight = (withVideo: string) => {
    adjustSlideshowHeight();
    if (withVideo === 'withVideo') {
      adjustVideoHeight();
    }
  };

  const { setNewValue } = useSearchParamHelpers(undefined, { replace: true });
  const { modelPicker, previewImagesToShow } = imagesData;
  const shouldShowModelInfoForMobile = modelPicker
    ? !forceSuppressModelChanger && isMobile && modelPicker?.shouldRenderModelChanger
    : false;
  const videoSourcesForSet = window.server_pdp_data?.product.media.find((media) => media.media_type === 'video')
    ?.sources;

  const productVideos = !pdpData.isASet
    ? getVideosByColor(userSelections, pdpData)
    : videoSourcesForSet
    ? videoSourcesForSet.filter((source) => source.format === 'mp4' && source.height > 720)
    : [];

  const clearScrollLeft = () => {
    if (isMobile && slider.current) {
      slider.current.scrollLeft = 0; // Workaround for Chrome bug with scroll-snap-type
    }
  };

  const videoQty = productVideos.length;

  // zoom image
  const [zoomImageIndex, setZoomImageIndex] = useState(-1);
  const zoomImageExists = !!previewImagesToShow[zoomImageIndex];
  const setZoomImageIndexFunc = (index: number) => () => setZoomImageIndex(index);
  const closeZoom = () => setZoomImageIndex(-1);

  return (
    <div className="product__images">
      {zoomImageExists && (
        <Suspense
          // ? [REVISIT] loading ux
          fallback={null}
        >
          <FullScreenPhotoSwipe
            isOpen={zoomImageExists}
            options={{ index: zoomImageIndex }}
            images={previewImagesToShow}
            handleClose={closeZoom}
          />
        </Suspense>
      )}
      <Fragment>
        {shouldShowModelInfoForMobile && modelPicker ? (
          <Suspense
            // ? [REVISIT] loading ux
            fallback={null}
          >
            <ModelChangerUI
              modelOptions={modelPicker.options}
              onChange={(e, data) => {
                if (data.value && typeof data.value === 'string') setNewValue('modelCode', data.value);
              }}
              value={modelPicker.value}
            />
          </Suspense>
        ) : null}
        <div ref={slider} className="main__images--wrapper" onLoad={clearScrollLeft}>
          {previewImagesToShow.map((image, index) => {
            const onlyOneImage = previewImagesToShow.length === 1 ? 'single-image' : '';
            const shouldShowModelInfoForDesktop =
              (!forceSuppressModelChanger && !isMobile && previewImagesToShow.length > 1 && index === 1) ||
              (!forceSuppressModelChanger && !isMobile && onlyOneImage)
                ? true
                : false;

            return (
              <div
                className="image-wrapper"
                style={
                  !isMobile
                    ? { width: '50%', position: 'relative' }
                    : {
                        minWidth: '100vw',
                        position: 'relative',
                      }
                }
              >
                <img
                  tabIndex={0}
                  id={`Image-${index}`}
                  className={
                    previewImagesToShow.length === 1
                      ? 'featured__image lazyload blur-up featured__image--full'
                      : 'featured__image lazyload blur-up'
                  }
                  key={image}
                  src={image}
                  width={750}
                  height={1125}
                  // ? [REVISIT] TITLE
                  alt={`${pdpData.pdpProductShell.title}--${index}`}
                  onClick={setZoomImageIndexFunc(index)}
                  onKeyDown={(e) => {
                    e.key === 'Enter' && setZoomImageIndex(index); // Activate zoom on Enter press
                  }}
                  onLoad={() => {
                    return productVideos && productVideos.length > 0 && index === 0
                      ? adjustHeight('withVideo')
                      : index === 0
                      ? adjustHeight('')
                      : null;
                  }}
                />
                {shouldShowModelInfoForDesktop && modelPicker ? (
                  <Suspense
                    // ? [REVISIT] loading ux
                    fallback={null}
                  >
                    <ModelChangerUI
                      modelOptions={modelPicker.options}
                      onChange={(e, data) => {
                        if (data.value && typeof data.value === 'string') setNewValue('modelCode', data.value);
                      }}
                      value={modelPicker.value}
                      wrapperClass={onlyOneImage}
                    />
                  </Suspense>
                ) : null}
              </div>
            );
          })}
          {productVideos && productVideos.length > 0 ? (
            productVideos.map((video, index) => (
              <div className="video__wrapper" key={video.url}>
                <video
                  className="featured__video"
                  poster={previewImagesToShow[index]}
                  preload="none"
                  controlsList="nodownload"
                  playsInline
                >
                  <source className="video__source" src={video.url} type={video.mimeType} />
                </video>
                <img
                  tabIndex={0}
                  role="button"
                  className="img-fluid video__play-icon"
                  src="https://cdn.shopify.com/s/files/1/2185/2813/files/play-icon-pdp.svg?v=1587668364"
                  alt="Play"
                  onClick={playVideo}
                  onKeyDown={(e) => {
                    e.key === 'Enter' && playVideo(e);
                  }}
                />
              </div>
            ))
          ) : isMobile ? (
            <div className="video__wrapper" style={{ width: '100px', height: '1px', paddingRight: '0' }} />
          ) : null}
          {children}
          <AlmostGoneImageMessage pdpData={pdpData} userSelections={userSelections} />
        </div>
        {previewImagesToShow.length > 1 && isMobile ? (
          <CustomScrollBar
            imageCount={previewImagesToShow.length}
            videoQty={videoQty}
            sliderContainer={slider}
            viewPortWidth={viewPortWidth}
          />
        ) : null}
      </Fragment>
    </div>
  );
};

export default ProductImages;
